import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import {
  ServiceSearchResult,
  useServiceSearchQuery,
} from "@/hooks/queries/useServiceSearchQuery";
import { useSelectableListNavigation } from "@/hooks/useSelectableListNavigation";
import { useNetworxServices } from "@/hooks/queries/useNetworxServices";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_TIME = 200;
const BLUR_DELAY_MS = 150; // Allow click events to fire before closing

export function useSearchServicesInput() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedService, setSelectedService] = useState<ServiceSearchResult | null>(null);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const isSearchEnabled = searchQuery.length >= MIN_QUERY_LENGTH;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { services, isFetching, query: servicesQuery } = useServiceSearchQuery(searchQuery, {
    debounceTime: DEBOUNCE_TIME,
    minQueryLength: MIN_QUERY_LENGTH,
  });
  const { data: servicesData } = useNetworxServices({
    sortBy: "order",
  });

  const hasResults = services.length > 0;
  const mainServices = servicesData?.mainServices ?? [];

  const normalizedSearchQuery = searchQuery.trim();

  const shouldShowQueryResults =
    normalizedSearchQuery.length >= MIN_QUERY_LENGTH
    && servicesQuery.trim() === normalizedSearchQuery
    && hasResults;
    
  const modalServices = shouldShowQueryResults
    ? services.map((service) => ({
        id: service.id,
        name: service.service_name,
        href: service.uri,
      }))
    : mainServices.map((service) => ({
        id: service.id,
        name: service.service_name,
        href: service.pathname,
      }));

  const updateSearchQuery = (value: string) => {
    setSearchQuery(value);
    setSelectedService(null);
    setIsServicesModalOpen(false);
    setSelectedIndex(-1);
  };

  const selectService = (service: ServiceSearchResult) => {
    setSelectedService(service);
    setIsServicesModalOpen(false);
    setSearchQuery(service.service_name);
    setIsDropdownOpen(false);
    setSelectedIndex(-1);
  };

  const submitSearch = () => {
    if (selectedService) {
      window.location.href = selectedService.uri;
      return;
    }

    if (shouldShowQueryResults) {
      const exactMatch = findExactMatchService(services, normalizedSearchQuery);
      if (exactMatch) {
        window.location.href = exactMatch.uri;
        return;
      }
    }

    closeResultsDropdown();
    setIsServicesModalOpen(true);
  };

  const closeServicesModal = () => {
    setIsServicesModalOpen(false);
  };

  const closeResultsDropdown = () => {
    // Handle search button click or Enter key
    // For now, just close dropdown - can be extended later
    setIsDropdownOpen(false);
    setSelectedIndex(-1);
  };

  const openResultsDropdown = () => {
    if (selectedService) {
      return;
    }

    if (isSearchEnabled && (hasResults || isFetching)) {
      setIsDropdownOpen(true);
    }
  };

  const isDropdownNavigationEnabled = isDropdownOpen && hasResults;

  const { selectedIndex, setSelectedIndex, itemRefs, handleKeyDown } =
    useSelectableListNavigation({
      items: services,
      isEnabled: isDropdownNavigationEnabled,
      onSelect: selectService,
      onClose: closeResultsDropdown,
    });

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const hasHighlightedResult =
        isDropdownNavigationEnabled
        && selectedIndex >= 0
        && selectedIndex < services.length;

      if (hasHighlightedResult) {
        handleKeyDown(e);
        return;
      }

      e.preventDefault();
      submitSearch();
      return;
    }

    if (isDropdownNavigationEnabled) {
      handleKeyDown(e);
    }
  };

  // Update dropdown open state - keep open if we have services OR if we're loading
  useEffect(() => {
    if (selectedService) return;
    if (isSearchEnabled && (hasResults || isFetching)) {
      setIsDropdownOpen(true);
    }
  }, [isSearchEnabled, hasResults, isFetching, selectedService]);

  /**
   * if dropdown contains the active element, don't close it
   * otherwise, close it after a delay
   */
  const handleInputBlur = (e: React.FocusEvent) => {
    // Don't close if clicking on dropdown
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    // Use setTimeout to allow click events on dropdown items to fire
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
      }
    }, BLUR_DELAY_MS);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[role="textbox"]')
      ) {
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, setSelectedIndex]);

  return {
    searchQuery,
    updateSearchQuery,
    openResultsDropdown,
    handleInputBlur,
    handleInputKeyDown,
    closeResultsDropdown,
    isDropdownOpen,
    dropdownRef,
    services,
    isFetching,
    selectedIndex,
    itemRefs,
    isServicesModalOpen,
    modalServices,
    closeServicesModal,
    selectService,
    submitSearch,
    setSelectedIndex,
  };
}

function findExactMatchService(
  services: ServiceSearchResult[],
  query: string,
): ServiceSearchResult | undefined {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return undefined;
  }

  return services.find(
    (service) =>
      service.service_name.trim().toLowerCase() === normalizedQuery,
  );
}
